import {maxBy} from 'lodash';
import React, {useMemo} from 'react';
import {FormattedMessage} from 'react-intl';
import URL, {MODEL_SUB_OP} from '../../../config/url';
import {UnparsedJson} from '../../../types/utility/UnparsedJson';
import './SegmentationResult.scss';
import {DoubleImageCard} from '../SimpleImageCard/DoubleImageCard';
import {Box, Typography} from '@material-ui/core';
import {SegmentationLegend} from '../../../types/model/Model';
import {DeploymentClassificationResult} from '../../../types/deployment/DeploymentClassificationResult';
import SegmentationLegendView from './SegmentationLegendView';

const IMAGE_WIDTH = 125;
const IMAGE_HEIGHT = 125;

export type SegmentationResultProps = {
  search: string;
  data: UnparsedJson;
  projectId: string;
  deploymentId: string;
  segmentationLegends: SegmentationLegend[];
};

export function SegmentationResult({
  search,
  data,
  projectId,
  deploymentId,
  segmentationLegends,
}: SegmentationResultProps) {
  const [images, legends] = useMemo(() => {
    // TODO: fix backend for empty inferenceResults
    if (data === '""') {
      return [[], []];
    }

    let images: DeploymentClassificationResult[] = JSON.parse(data);
    if (images == null) {
      return [[], []];
    }

    if (search) {
      images = images.filter(image => {
        const topResult = maxBy(image.stats, row => parseFloat(row.progress ?? '0'));
        return topResult?.label?.toLowerCase().includes(search.toLowerCase()) ?? false;
      });
    }

    return [images ?? [], segmentationLegends];
  }, [data, segmentationLegends, search]);

  function ImageUrl(
    filename: string | undefined,
    direction: string,
    width?: number,
    height?: number
  ) {
    var url = `${URL.DATASET(projectId, MODEL_SUB_OP.GET_BY_ID)}?id=${filename?.replace(
      ',',
      '%252C'
    )}&deploymentId=${deploymentId}&dataPurpose=${direction}&cachebust=${Date.now()}`;
    if (width && height) {
      url += `&resolution=${width}:${height}`;
    }
    return url;
  }

  return (
    <div className="legend-grid-container">
      <SegmentationLegendView legends={legends} />
      <div className="inference-grid-container">
        <div className="view-grid">
          {images.length === 0 ? (
            <div className="segmentation-result__no-results">
              <FormattedMessage id="test.noResults" />
            </div>
          ) : (
            images.map((image, i) => (
              <Box>
                <DoubleImageCard
                  key={i}
                  imageL={(w?: number, h?: number) =>
                    ImageUrl(image.filename, 'OUTPUT', w, h)
                  }
                  imageR={(w?: number, h?: number) =>
                    ImageUrl(image.filename, 'INPUT', w, h)
                  }
                  width={IMAGE_WIDTH}
                  height={IMAGE_HEIGHT}
                  data-testid="segmentation-inference-result"
                  className="segmentation-result__image-card"
                  showImageMetadata={true}
                />
                <Typography
                  className="image-card-horizontal_text"
                  variant="caption"
                  color="textSecondary"
                  component="div"
                  align="center"
                  noWrap
                >
                  {image.filename}
                </Typography>
              </Box>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
